// Set global EventEmitter max listeners to prevent memory leak warnings from Apollo Client subscriptions
import { EventEmitter } from 'events';
import * as Sentry from '@sentry/react';
import { StrictMode, useEffect } from 'react';
import * as ReactDOM from 'react-dom/client';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';
import { AppContainer } from './App';
import { installNumberInputScrub } from './utils/numberScrub';

EventEmitter.defaultMaxListeners = 20;

// Auto-reload when a deployment invalidates cached JS chunks
// (prevents "SyntaxError: Unexpected token '<'" Sentry errors)
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

if (import.meta.env.PROD)
  Sentry.init({
    dsn: 'https://6e70747785b5401dacda1d4392c9679f@o1342113.ingest.sentry.io/6615889',
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    // Outlook Safe Links / Defender link scanner bot, not real users (getsentry/sentry-javascript#3440)
    ignoreErrors: [/Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/],
    environment: import.meta.env.VITE_NX_SENTRY_ENV,
    enabled: !!import.meta.env.VITE_NX_SENTRY_ENV,
  });

// On page load or when changing themes, best to add inline in `head` to avoid FOUC
if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
  document.documentElement.classList.add('dark');
} else {
  document.documentElement.classList.remove('dark');
}

// Prevent browser zoom on Ctrl+scroll and Ctrl+plus/minus/zero
window.addEventListener(
  'wheel',
  (e) => {
    if (e.ctrlKey) e.preventDefault();
  },
  { passive: false },
);
window.addEventListener('keydown', (e) => {
  if (e.ctrlKey && (e.key === '+' || e.key === '-' || e.key === '=' || e.key === '0')) {
    e.preventDefault();
  }
});

// Every number field in the app: drag sideways to scrub, click to type (Blender-style).
installNumberInputScrub();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <StrictMode>
    <AppContainer />
  </StrictMode>,
);
